import AccItemParamRow, {
    AccItemParamRowDTO,
    BokParamType,
    Lookup,
    accItemParamRowDefaultProps,
} from "../models/AccItemParamRow";
import AccItemParamApi from "./AccItemParamApi";
import busyIndication from "../../../utils/busyIndication";
import eventBus from "../../../services/eventBus";

export class AccItemParamStore {
    paramTypes: BokParamType[] = [];
    rows: AccItemParamRow[] = [];
    brands: Lookup[] = [];
    depots: Lookup[] = [];
    statGroups: Lookup[] = [];
    divisionItems: Lookup[] = [];

    private snapshot: AccItemParamRow | null = null;

    @busyIndication()
    async load() {
        const [paramTypes, brands, depots, statGroups, divisionItems, rows] = await Promise.all([
            AccItemParamApi.getParamTypes(),
            AccItemParamApi.getBrands(),
            AccItemParamApi.getDepots(),
            AccItemParamApi.getStatGroups(),
            AccItemParamApi.getDivisionItems(),
            AccItemParamApi.getRows(),
        ]);
        this.paramTypes = paramTypes;
        this.brands = brands;
        this.depots = depots;
        this.statGroups = statGroups;
        this.divisionItems = divisionItems;
        this.rows = rows.map((r) => this.toGridRow(r));
    }

    @busyIndication()
    async loadRows() {
        this.rows = (await AccItemParamApi.getRows()).map((r) => this.toGridRow(r));
    }

    private toGridRow(dto: AccItemParamRowDTO): AccItemParamRow {
        const row: AccItemParamRow = {
            ...accItemParamRowDefaultProps,
            brand: dto.brand,
            brandName: dto.brandName,
            depot: dto.depot,
            depotName: dto.depotName,
            statGroup: dto.statGroup,
            statGroupName: dto.statGroupName,
            divItem: dto.divItem,
            divItemName: dto.divItemName,
        };
        for (const type of this.paramTypes) {
            row[`pt${type.id}`] = dto.values.find((v) => v.paramType == type.id)?.param ?? "";
        }
        return row;
    }

    addRow() {
        if (this.rows.some((r) => r.isNew)) {
            return;
        }
        const row: AccItemParamRow = { ...accItemParamRowDefaultProps, inEdit: true, isNew: true };
        for (const type of this.paramTypes) {
            row[`pt${type.id}`] = "";
        }
        this.rows = [row, ...this.rows];
    }

    setEditing(row: AccItemParamRow) {
        this.snapshot = { ...row };
        row.inEdit = true;
        this.rows = [...this.rows];
    }

    cancelEdit(row: AccItemParamRow) {
        if (row.isNew) {
            this.rows = this.rows.filter((r) => r != row);
            return;
        }
        if (this.snapshot) {
            Object.assign(row, this.snapshot, { inEdit: false });
        } else {
            row.inEdit = false;
        }
        this.snapshot = null;
        this.rows = [...this.rows];
    }

    async saveRow(row: AccItemParamRow) {
        if (!row.brand || !row.depot || !row.statGroup || !row.divItem) {
            eventBus.$emit("error", "A gyártmány, telephely, cikkstat és aldivízió megadása kötelező!");
            return;
        }

        const duplicate = this.rows.some(
            (r) =>
                r != row &&
                r.brand == row.brand &&
                r.depot == row.depot &&
                r.statGroup == row.statGroup &&
                r.divItem == row.divItem
        );
        if (duplicate) {
            eventBus.$emit("error", "Ilyen gyártmány-telephely-cikkstat-aldivízió kombinációjú sor már létezik!");
            return;
        }

        try {
            await AccItemParamApi.saveRow({
                originalBrand: row.isNew ? 0 : this.snapshot?.brand ?? row.brand,
                originalDepot: row.isNew ? 0 : this.snapshot?.depot ?? row.depot,
                originalStatGroup: row.isNew ? 0 : this.snapshot?.statGroup ?? row.statGroup,
                originalDivItem: row.isNew ? 0 : this.snapshot?.divItem ?? row.divItem,
                brand: row.brand,
                depot: row.depot,
                statGroup: row.statGroup,
                divItem: row.divItem,
                values: this.paramTypes.map((type) => ({
                    paramType: type.id,
                    param: row[`pt${type.id}`] ?? "",
                })),
            });
        } catch (err) {
            console.error(err);
            return;
        }

        this.snapshot = null;
        eventBus.$emit("success", "Sikeres mentés!");
        await this.loadRows();
    }

    setDeleting(row: AccItemParamRow) {
        row.deleting = !row.deleting;
        this.rows = [...this.rows];
    }

    async deleteRow(row: AccItemParamRow) {
        try {
            await AccItemParamApi.removeRow(row.brand, row.depot, row.statGroup, row.divItem);
        } catch (err) {
            console.error(err);
            return;
        }

        eventBus.$emit("success", "Sikeres törlés!");
        await this.loadRows();
    }
}

const store = new AccItemParamStore();
export default store;
